// End-to-end encrypted account sync (D-139; design: 02_ENGINEERING/Cloud_Sync.md).
// The user's personal data (watchlists, setups, journal, …) follows them across
// devices. Every value is AES-GCM encrypted with a key DERIVED FROM THE LOGIN, so
// the server stores only ciphertext and can never read it — the Kernel journal
// rule (encrypted) is honoured for the whole blob. Last-write-wins per key.
import { getStoredLicense, apiBase } from "./license.js";
import { storageGet, storageSet, storageGetAll, onStorageChange } from "./storage.js";

// The personal-data keys that follow the account. NOT device secrets
// (license/finnhubKey), caches, or transient UI — see Cloud_Sync.md.
const SYNC_KEYS = [
  "watchlistTickers",
  "watchlistAlerts",
  "watchlistTimeframes",
  "wlBoards",
  "wlCurrent",
  "wlWorking",
  "wlSetupLibrary",
  "wlRelSettings",
  "wlLocked",
  "journalEntries",
  "journalCustomTags",
  "journalTraders",
  "journalAccountSize",
  "journalPointValue",
  "journalRiskPercent",
  "strategies",
  "strategySignalSubs",
  "chartDrawnLines",
  "referenceTrades",
];
const META_KEY = "cloudSyncMeta"; // device-local: per-key last-synced timestamp (LWW)

// Keys currently being written FROM a server pull — so the storage-change listener
// doesn't immediately push them straight back up (echo suppression).
const applying = new Set<string>();

// --- crypto ---------------------------------------------------------------

const enc = new TextEncoder();
const dec = new TextDecoder();

async function deriveKey(email: string, licenseKey: string): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(licenseKey) as BufferSource, "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: enc.encode(email.trim().toLowerCase()) as BufferSource, iterations: 150000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function unb64(str: string): Uint8Array {
  const s = atob(str);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

async function encryptValue(key: CryptoKey, plaintext: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, enc.encode(plaintext) as BufferSource));
  const joined = new Uint8Array(iv.length + ct.length);
  joined.set(iv, 0);
  joined.set(ct, iv.length);
  return b64(joined);
}

async function decryptValue(key: CryptoKey, ctB64: string): Promise<string | null> {
  try {
    const raw = unb64(ctB64);
    const iv = raw.slice(0, 12);
    const ct = raw.slice(12);
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: iv as BufferSource }, key, ct as BufferSource);
    return dec.decode(pt);
  } catch {
    return null; // wrong key / corrupt — skip this row
  }
}

// --- meta (per-key last-synced timestamp) ---------------------------------

async function getMeta(): Promise<Record<string, number>> {
  return storageGet<Record<string, number>>(META_KEY, {});
}
async function setMetaKey(k: string, ts: number): Promise<void> {
  const m = await getMeta();
  m[k] = ts;
  await storageSet(META_KEY, m);
}

// --- push / pull ----------------------------------------------------------

async function pushKey(key: CryptoKey, k: string): Promise<void> {
  const license = await getStoredLicense();
  if (!license?.token) return;
  const all = await storageGetAll();
  if (!(k in all)) return; // nothing stored for this key
  const ct = await encryptValue(key, JSON.stringify(all[k]));
  const updatedAt = Date.now();
  const base = await apiBase();
  const res = await fetch(`${base}/v1/sync`, {
    method: "POST",
    headers: { authorization: `Bearer ${license.token}`, "content-type": "application/json" },
    body: JSON.stringify({ items: [{ k, ct, updatedAt }] }),
  }).catch(() => null);
  if (res?.ok) await setMetaKey(k, updatedAt);
}

async function pullAll(key: CryptoKey): Promise<void> {
  const license = await getStoredLicense();
  if (!license?.token) return;
  const base = await apiBase();
  const res = await fetch(`${base}/v1/sync`, { headers: { authorization: `Bearer ${license.token}` } }).catch(() => null);
  if (!res?.ok) return;
  const items = ((await res.json()) as { items?: Array<{ k: string; ct: string; updatedAt: number }> }).items ?? [];
  const meta = await getMeta();
  for (const it of items) {
    if (!SYNC_KEYS.includes(it.k)) continue;
    if (it.updatedAt <= (meta[it.k] ?? 0)) continue; // local copy is newer/equal
    const plain = await decryptValue(key, it.ct);
    if (plain == null) continue;
    let value: unknown;
    try {
      value = JSON.parse(plain);
    } catch {
      continue;
    }
    applying.add(it.k); // suppress the echo push from the change listener
    await storageSet(it.k, value);
    await setMetaKey(it.k, it.updatedAt);
  }
}

/** First-ever upload of local keys the server hasn't seen (meta unset), so a device
 *  that had data before sync existed seeds the account without clobbering others. */
async function pushLocalInitial(key: CryptoKey): Promise<void> {
  const all = await storageGetAll();
  const meta = await getMeta();
  for (const k of SYNC_KEYS) {
    if (k in all && meta[k] == null) await pushKey(key, k);
  }
}

let started = false;

/** Start account sync: pull the account's data down, seed any local-only data up,
 *  then keep pushing on every change. Signed-in users only; safe to call on open. */
export async function startCloudSync(): Promise<void> {
  if (started) return;
  const license = await getStoredLicense();
  if (!license?.token || !license.email || !license.licenseKey) return;
  const key = await deriveKey(license.email, license.licenseKey);
  started = true;
  await pullAll(key);
  await pushLocalInitial(key);
  for (const k of SYNC_KEYS) {
    onStorageChange(k, () => {
      if (applying.has(k)) {
        applying.delete(k);
        return;
      }
      void pushKey(key, k);
    });
  }
}
